import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, X, Flame, Share2, Link2, MessageCircle, Stamp } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { challengeNumber, squares } from "@/lib/daily";
import { useDailyGame, optionClass } from "@/lib/useDailyGame";
import { liveStreak, streakLabel } from "@/lib/engagement";
import { socialLinks, canNativeShare, nativeShare, copyText } from "@/lib/share";
import { trackEvent } from "@/lib/analytics";
import { storage } from "@/lib/storage";
import Leaderboard, { NicknameForm } from "@/components/Leaderboard";
import PushToggle from "@/components/PushToggle";
import ChallengeFriend, { ChallengeBanner, Versus } from "@/components/ChallengeFriend";
import { INTRO, REGION_OF } from "@/lib/introContent";

const IN = { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0 } };

const promptValue = (q, lang) => {
  if (q.type === "capital") return q.target.capital?.[lang];
  if (q.type === "fauna") return q.target.nature?.fauna?.[lang];
  if (q.type === "region") return (INTRO[lang] || INTRO.en).regions[REGION_OF[q.target.code]?.id]?.name;
  return q.target.currency?.[lang];
};

function Prompt({ q, lang, t }) {
  if (q.type === "flag") return <img src={q.target.flag_url} alt="" className="w-48 h-32 sm:w-64 sm:h-44 object-cover rounded-xl shadow-md border border-stoneLine" data-testid="daily-flag" />;
  const value = promptValue(q, lang);
  return (
    <div className="text-center" data-testid="daily-prompt">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{t(`daily_q_${q.type}`)}</div>
      <div className="mt-2 font-display font-bold text-2xl sm:text-3xl text-ink">{value}</div>
    </div>
  );
}

function Result({ picks, streak, day, vs }) {
  const { t, lang } = useLang();
  const [copied, setCopied] = useState(false);
  const [nick, setNick] = useState(() => storage.getJSON(`atlas.daily.${day}`, {})?.nickname || "");
  const saveNick = (n) => { setNick(n); storage.setJSON(`atlas.daily.${day}`, { ...storage.getJSON(`atlas.daily.${day}`, {}), nickname: n }); };
  const score = picks.filter(Boolean).length;
  const url = `${window.location.origin}/quiz?daily=1&lang=${lang}`;
  const text = `${t("daily_share").replace("{n}", challengeNumber(day)).replace("{score}", `${score}/5`)} ${squares(picks)}`;
  const links = socialLinks(url, text);
  const track = (method) => trackEvent("share", { method, content_type: "daily_challenge", lang });
  const copy = async () => { track("copy"); await copyText(`${text}\n${url}`); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  return (
    <motion.div {...IN} className="w-full flex flex-col items-center gap-4" data-testid="daily-result">
      <div className="font-display font-bold text-3xl text-ink">{score}/5</div>
      <div className="text-3xl tracking-wider" data-testid="daily-result-squares">{squares(picks)}</div>
      <div className="inline-flex items-center gap-1.5 text-sm font-semibold text-ochre" data-testid="daily-result-streak"><Flame className="w-4 h-4" fill="currentColor" /> {streakLabel(t, streak)}</div>
      {vs && <Versus id={vs} picks={picks} day={day} />}
      {nick ? <p className="text-xs text-oasis font-medium inline-flex items-center gap-1" data-testid="score-saved"><Check className="w-3.5 h-3.5" /> {t("nick_saved").replace("{nick}", nick)}</p> : <NicknameForm day={day} picks={picks} onDone={saveNick} />}
      <p className="text-sm text-muted-foreground text-center">{t("daily_come_back")}</p>
      <PushToggle />
      <div className="flex flex-wrap justify-center gap-2">
        <a href={links.whatsapp} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp")} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#25D366]/15 text-[#128C7E] text-sm font-medium" data-testid="daily-share-whatsapp"><MessageCircle className="w-4 h-4" /> WhatsApp</a>
        <button onClick={copy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-stoneLine text-sm font-medium hover:bg-sandDeep/50" data-testid="daily-share-copy">{copied ? <Check className="w-4 h-4 text-oasis" /> : <Link2 className="w-4 h-4" />} {copied ? t("copied") : t("share")}</button>
        {canNativeShare() && <button onClick={() => { track("native"); nativeShare({ title: text, text, url }); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-ink text-sand text-sm font-medium" data-testid="daily-share-native"><Share2 className="w-4 h-4" /> {t("share")}</button>}
      </div>
      <ChallengeFriend day={day} picks={picks} rematch={!!vs} />
      <Link to={`/passport?lang=${lang}`} className="inline-flex items-center gap-1.5 text-xs text-ochre hover:underline" data-testid="daily-to-passport"><Stamp className="w-3.5 h-3.5" /> {t("passport")}</Link>
    </motion.div>
  );
}

function Option({ c, q, answered, lang, onPick }) {
  const isTarget = c.code === q.target.code;
  const isPicked = answered?.picked === c.code;
  return (
    <button onClick={() => onPick(c.code)} disabled={!!answered} data-testid={`daily-option-${c.code}`}
      className={`px-4 py-3 rounded-xl border text-start transition-colors flex items-center justify-between gap-2 ${optionClass(answered, isTarget, isPicked)}`}>
      <span className="flex items-center gap-2 min-w-0"><img src={c.flag_url} alt="" className={`w-6 h-4 object-cover rounded-sm border border-stoneLine ${q.type === "flag" ? "hidden" : ""}`} /><span className="font-medium truncate">{c.short_names[lang]}</span></span>
      {answered && isTarget && <Check className="w-4 h-4 shrink-0" />}
      {answered && isPicked && !isTarget && <X className="w-4 h-4 shrink-0" />}
    </button>
  );
}

function Progress({ questions, index, picks }) {
  const tone = (k) => (k < index ? (picks[k] ? "bg-oasis" : "bg-ochre") : k === index ? "bg-ink" : "bg-sandDeep");
  return <div className="flex gap-1.5" aria-hidden>{questions.map((qq, k) => <span key={qq.target.code} className={`w-6 h-1.5 rounded-full ${tone(k)}`} />)}</div>;
}

function Round({ game, lang, t }) {
  const { question: q, index, answered, isLast, pick, next } = game;
  return (
    <>
      <Progress questions={game.questions} index={index} picks={game.picks} />
      <AnimatePresence mode="wait"><motion.div key={index} {...IN}><Prompt q={q} lang={lang} t={t} /></motion.div></AnimatePresence>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
        {q.options.map((c) => <Option key={c.code} c={c} q={q} answered={answered} lang={lang} onPick={pick} />)}
      </div>
      <AnimatePresence>
        {answered && (
          <motion.div {...IN} className="w-full flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm" data-testid="daily-feedback">{answered.correct ? <span className="text-oasis font-semibold">{t("quiz_correct")}</span> : <span className="text-ochre">{t("quiz_wrong")} <b>{q.target.short_names[lang]}</b></span>}</div>
            <button onClick={next} className="px-5 py-2.5 rounded-full bg-ink text-sand hover:bg-ochre transition-colors text-sm font-medium" data-testid="daily-next">{isLast ? t("daily_finish") : t("quiz_next")}</button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

/** Daily Challenge: 5 date-seeded questions, one attempt per day, shareable result + streak. */
export default function DailyChallenge({ countries }) {
  const { lang, t } = useLang();
  const [params] = useSearchParams();
  const vs = params.get("vs");
  const game = useDailyGame(countries, lang);
  const { day, questions, saved, index } = game;
  if (!questions.length) return null;

  return (
    <div className="rounded-3xl border border-stoneLine bg-card p-6 sm:p-10 flex flex-col items-center gap-8" data-testid="daily-challenge">
      {vs && <div className="w-full"><ChallengeBanner id={vs} /></div>}
      <div className="w-full flex items-center justify-between text-sm">
        <span className="inline-flex items-center gap-1.5 font-semibold text-ink"><Flame className="w-4 h-4 text-ochre" /> {t("daily_challenge")} <span className="text-muted-foreground">#{challengeNumber(day)}</span></span>
        {!saved && <span className="text-muted-foreground" data-testid="daily-progress">{index + 1} / {questions.length}</span>}
      </div>
      {saved ? <Result picks={saved.picks} streak={liveStreak()} day={day} vs={vs} /> : <Round game={game} lang={lang} t={t} />}
      {saved && <div className="w-full"><Leaderboard /></div>}
    </div>
  );
}
